import { useLocation } from 'react-router';

import { sessionIdentity } from '@/auth/session';
import { useAuth } from '@/auth/useAuth';
import { confirmSignOut } from '@/field/signOutGuard';
import { useModules } from '@/modules/ModuleContext';
import { Button } from '@/ui/Button';
import { Wordmark } from '@/ui/Wordmark';
import { IdentityMenu } from './IdentityMenu';
import { locatePath, railFor } from './navigation';
import { RailItem } from './RailItem';

export interface NavRailProps {
  /** Whether labels are shown beside the glyphs. */
  readonly expanded: boolean;
  /** Toggles the expanded state. */
  readonly onToggle: () => void;
}

/**
 * The primary navigation.
 *
 * A vertical rail shaped by role: it carries the destinations the signed-in person's roles pin,
 * which is four for a consultant and ten for an owner. That is few enough to read at a glance, so
 * there are no group headings; the rail is still the one navigation landmark, so the wordmark,
 * the identity menu and sign out are inside it and a skip link can land on it.
 *
 * Collapsing keeps the glyphs and drops the words. The labels stay in the accessible name of each
 * link, so a collapsed rail is narrower, not less usable: a screen reader reads exactly the same
 * navigation either way.
 *
 * A link opens the first view of its destination the person may open, and stays marked as the
 * current page anywhere inside the destination: on a record, or on another of its views.
 *
 * Two links carry a count: Sales, for the deals that need attention, and Today, for everything
 * waiting for the person. A count is part of its link's name, and Today's changes are announced.
 *
 * Items the signed-in user cannot reach are not rendered. That is a courtesy to them, not a
 * security measure — the route guard and the server each refuse independently.
 *
 * @param props see {@link NavRailProps}
 * @returns the navigation rail
 */
export function NavRail({ expanded, onToggle }: NavRailProps): React.JSX.Element {
  const { user, can, signOut } = useAuth();
  const enabled = useModules();
  const { pathname } = useLocation();
  const current = locatePath(pathname)?.destination.id;
  const rail = railFor({ roles: user?.roles ?? [], can, enabled });

  return (
    // The rail *is* the navigation landmark, rather than containing one. Wrapping
    // only the links would leave the wordmark, the identity menu and sign out
    // outside every landmark on the page, which axe reports and which leaves a
    // screen-reader user with content they can only reach by walking the whole
    // document.
    //
    // tabIndex lets the "Skip to navigation" link move focus here; a landmark is
    // not focusable by default.
    <nav className="fo-rail" id="navigation" aria-label="Main" tabIndex={-1}>
      <div className="fo-rail__brand">
        <Wordmark markOnly={!expanded} />
        <Button
          variant="ghost"
          size="sm"
          icon={expanded ? 'chevron-left' : 'chevron-right'}
          iconOnly
          aria-expanded={expanded}
          aria-controls="navigation"
          onClick={onToggle}
        >
          {expanded ? 'Collapse navigation' : 'Expand navigation'}
        </Button>
      </div>

      <div className="fo-rail__nav">
        <ul className="fo-rail__list">
          {rail.map(({ destination, path }) => (
            <RailItem
              key={destination.id}
              destination={destination}
              path={path}
              current={destination.id === current}
            />
          ))}
        </ul>
      </div>

      <div className="fo-rail__footer">
        <IdentityMenu />
        <Button
          variant="ghost"
          size="sm"
          icon="sign-out"
          aria-label="Sign out"
          block
          onClick={() => {
            // Field work queued offline is keyed to this person; signing out strands it.
            void (async () => {
              if (await confirmSignOut(sessionIdentity(user))) void signOut();
            })();
          }}
        >
          Sign out
        </Button>
      </div>
    </nav>
  );
}
